// Every component the prototypes own, by name, from
// scripts/ds-local-components.mjs (refresh it in the session-start sweep).
//
// Drill mode used to know a component only if somebody had written its registry
// card, so an undocumented one was drawn as whatever MUI primitive happened to
// be its outermost element: ChatActionRow, a real wrapped component with a file
// of its own, read as "Stack — raw MUI". That is the tool lying about the code.
//
// So naming is a filesystem fact and lives here, and the registry is what it was
// always for — the notes. A name found here and not in the registry is a
// component with no notes yet, which is a different thing from raw MUI and the
// panel says so.

import localComponents from '@/designs-dev/inspect/local-components.json';

const FILES = localComponents.files as Record<string, string>;

// The same name in more than one file (two ChatBubbles, in the intake and in
// north-star). The scan cannot say which one an instance came from, so the name
// is still shown and the file is not asserted.
const AMBIGUOUS = new Set<string>(localComponents.ambiguous);

// The component library's own frames (Frame, ComponentSet, Phone) sit around
// the components it draws, inside the page drill mode reads. As layers they
// would swallow every click, so a Button would select as the Frame round it.
const LIBRARY_CHROME = '/designs-dev/library/';

/** Does the prototype own a component of this name? */
export const isLocalComponent = (name: string): boolean =>
  name in FILES && !FILES[name].includes(LIBRARY_CHROME);

/** Where it lives, when the scan is sure which file that is. */
export const localFileFor = (name: string): string | undefined =>
  AMBIGUOUS.has(name) ? undefined : FILES[name];

export type ComponentStory = { file: string; title: string; docsId: string };

const STORIES = localComponents.stories as Record<string, ComponentStory>;

/**
 * The component's story, by the file beside it. Storybook is where every state
 * of a component is laid out side by side, which is the one thing neither the
 * registry card nor a screen in the prototype can show.
 */
export const storyFor = (name: string): ComponentStory | undefined =>
  STORIES[name];

/**
 * Storybook runs beside the app on 6007 (`/loft storybook`), so the link only
 * means anything on localhost — on designs.dev there is no Storybook to open.
 */
export const storybookUrl = (docsId: string): string =>
  `http://localhost:6007/?path=/docs/${docsId}`;
